// frontend/src/components/player/VideoThemePlayer.tsx


import React, { useEffect, useRef, useState } from 'react';
import { TemplateType } from '../../types/Template';


declare global {
 interface Window {
   /** Filled by React before the theme script executes */
   __INVITE_DATA__?: Record<string, any>;
   /** Implemented by every theme script */
   initInvite?: (
     el: HTMLElement,
     data: Record<string, any>,
     opts: { mode: 'live' | 'preview' }
   ) => void;
   destroyInvite?: () => void;
   recordResponse?: (invitationId: string, response: string, peopleCount?: number) => Promise<any>;
   /** Counter for retry attempts */
   initRetryCount?: number;
 }
}


interface Props {
 template: TemplateType;
 data: Record<string, any>;
 mode?: 'live' | 'preview'; // default = 'live'
 onError?: (error: Error) => void;
}


const VideoThemePlayer: React.FC<Props> = ({
  template,
  data,
  mode = 'live',
  onError
 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
 
 
  useEffect(() => {
    if (!template.overlayBundle?.css || !template.overlayBundle?.js) {
      console.error('Missing overlay bundle CSS or JS');
      setLoadingState('error');
      setErrorMessage('Template resources missing');
      if (onError) onError(new Error('Template resources missing'));
      return;
    }
 
 
    let jsTag: HTMLScriptElement | null = null;
    let cssTag: HTMLLinkElement | null = null;
    let roundedObserver: MutationObserver | null = null;
    const templateInstanceId = `template-${template.id}-${Date.now()}`;
 
 
    // Clean up any existing template CSS before adding new one
    const existingTemplateCss = document.querySelectorAll('link[data-template-css]');
    existingTemplateCss.forEach(link => link.remove());
 
 
    try {
      const withVersion = (url: string) => {
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}v=${template.version ?? '1'}-${Date.now()}`;
      };
 
 
      cssTag = document.createElement('link');
      cssTag.rel = 'stylesheet';
      cssTag.href = withVersion(template.overlayBundle.css);
      cssTag.setAttribute('data-template-css', templateInstanceId);
      document.head.appendChild(cssTag);
 
 
      console.log('VideoThemePlayer - Template ID:', template.id);
      console.log('VideoThemePlayer - Passing data:', data);
 
 
      const onAccept = data.onAccept;
      const onDecline = data.onDecline;
 
 
      window.recordResponse = (invitationId: string, response: string, peopleCount?: number) => {
        console.log('recordResponse called with:', invitationId, response, peopleCount);
        if (response === 'attending' || response === 'accepted') {
          if (onAccept && typeof onAccept === 'function') {
            onAccept(peopleCount);
          }
        } else if (response === 'not_attending' || response === 'declined') {
          if (onDecline && typeof onDecline === 'function') {
            onDecline();
          }
        }
        return Promise.resolve({ data: { success: true } });
      };
 
 
      window.__INVITE_DATA__ = {
        ...data,
        musicUrl:  data.musicUrl  || data.customData?.musicUrl  || '',
        musicId:   data.musicId   || data.customData?.musicId   || '',
        musicName: data.musicName || data.customData?.musicName || '',
      };
 
 
      jsTag = document.createElement('script');
      jsTag.type = 'module';
      jsTag.src = withVersion(template.overlayBundle.js);
 
 
      const tryInit = () => {
        if (typeof window.initInvite === 'function' && containerRef.current) {
          try {
            // Clear any existing content before initializing
            containerRef.current.innerHTML = '';
           
            console.log('Initializing invitation with data:', window.__INVITE_DATA__);
            window.initInvite(containerRef.current, window.__INVITE_DATA__ || {}, { mode });
            setLoadingState('loaded');
 
 
            /* ðŸ”µ NEW â€” ensure host-uploaded video always has rounded corners */
            const keepCornersRounded = () => {
              const elements = containerRef.current?.querySelectorAll<HTMLElement>(
                'video, #suzy-video, .pixar-img-tall, [class*="pixar"], .video-container video'
              );
 
 
              elements?.forEach((el) => {
                el.style.setProperty('border-radius', '1rem', 'important');
                el.style.setProperty('overflow', 'hidden', 'important');
                el.style.setProperty('-webkit-border-radius', '1rem', 'important');
                el.style.setProperty('-moz-border-radius', '1rem', 'important');
 
 
                const parent = el.parentElement;
                if (parent && (parent.classList.contains('video-container') ||
                               parent.classList.contains('video-wrapper'))) {
                  parent.style.setProperty('border-radius', '1rem', 'important');
                  parent.style.setProperty('overflow', 'hidden', 'important');
                }
              });
            };
 
 
            keepCornersRounded();
            setTimeout(keepCornersRounded, 100);
            setTimeout(keepCornersRounded, 500);
 
 
            roundedObserver = new MutationObserver(() => {
              keepCornersRounded();
            });
            if (containerRef.current) {
              roundedObserver.observe(containerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class'],
              });
            }
          } catch (err) {
            console.error('Error initializing invitation:', err);
            setLoadingState('error');
            setErrorMessage('Failed to initialize invitation');
            if (onError) onError(err instanceof Error ? err : new Error(String(err)));
          }
        } else {
          if (window.initRetryCount === undefined) window.initRetryCount = 0;
          if (window.initRetryCount < 200) {
            window.initRetryCount++;
            setTimeout(tryInit, 50);
          } else {
            console.error('Failed to initialize invitation: initInvite function not available');
            setLoadingState('error');
            setErrorMessage('Failed to initialize invitation');
            if (onError) onError(new Error('initInvite function not available after timeout'));
          }
        }
      };
 
 
      jsTag.onload = () => {
        console.log('Theme script loaded, attempting to initialize...');
        window.initRetryCount = 0;
        tryInit();
      };
 
 
      jsTag.onerror = (event) => {
        console.error('[VideoThemePlayer] failed to load', jsTag?.src, event);
        setLoadingState('error');
        setErrorMessage('Failed to load theme script');
        if (onError) onError(new Error(`Failed to load script: ${jsTag?.src}`));
        if (containerRef.current) {
          containerRef.current.innerHTML = 'âš ï¸ Preview unavailable - script loading error';
        }
      };
 
 
      document.body.appendChild(jsTag);
 
 
    } catch (err) {
      console.error('Error in VideoThemePlayer setup:', err);
      setLoadingState('error');
      setErrorMessage(err instanceof Error ? err.message : String(err));
      if (onError) onError(err instanceof Error ? err : new Error(String(err)));
    }
 
 
    return () => {
      try {
        if (window.destroyInvite) window.destroyInvite();
      } catch (err) {
        console.error('Error cleaning up invitation:', err);
      }
      if (roundedObserver) roundedObserver.disconnect();
      if (cssTag) cssTag.remove();
      if (jsTag) jsTag.remove();
      delete window.__INVITE_DATA__;
      delete window.initInvite;
      delete window.destroyInvite;
      delete window.recordResponse;
      delete window.initRetryCount;
    };
  }, [template.id, template.overlayBundle?.css, template.overlayBundle?.js, template.version, data, mode, onError]);
 
 
  useEffect(() => {
    document.body.classList.add('invitation-preview-active');
    
    return () => {
      document.body.classList.remove('invitation-preview-active');
    };
  }, []);
 
 
  return (
    <div className="video-theme-player-container" style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      contain: 'layout style paint',
    }}>
      <div
        ref={containerRef}
        className={`video-theme-player ${mode === 'preview' ? 'preview-mode' : ''} ${loadingState === 'loaded' ? 'loaded' : ''}`}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          isolation: 'isolate',
        }}
      />
      {loadingState === 'loading' && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading invitation...</p>
        </div>
      )}
      {loadingState === 'error' && (
        <div className="error-overlay">
          <p>âš ï¸ Error: {errorMessage || 'Failed to load invitation'}</p>
        </div>
      )}
    </div>
  );
 };


export default VideoThemePlayer;